'use client';

import { useMemo } from 'react';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { nativeShare, type NativeShareVars } from './use-native-share';
import { writeClipboard } from './use-clipboard';

export const SHARE_OR_COPY_USED = {
  Share: 'share',
  Clipboard: 'clipboard',
} as const;
export type ShareOrCopyUsed = typeof SHARE_OR_COPY_USED[keyof typeof SHARE_OR_COPY_USED];

async function shareOrCopy(vars: NativeShareVars): Promise<{ used: ShareOrCopyUsed }> {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await nativeShare(vars);
      return { used: SHARE_OR_COPY_USED.Share };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw error;
    }
  }

  await writeClipboard({ text: vars.url });
  return { used: SHARE_OR_COPY_USED.Clipboard };
}

export function useShareOrCopy(): ActionResult<NativeShareVars, { used: ShareOrCopyUsed }> {
  return useActionHook<NativeShareVars, { used: ShareOrCopyUsed }>(
    useMemo(
      () => ({ mutationFn: shareOrCopy }),
      [],
    ),
  );
}
